import { cloneVNode, createVNode, isVNode, Fragment } from "vue";
import { isString, isFunction, isObject } from "./is.js";
import { toCamelCase, toKebabCase } from "./convert-case.js";
var ShapeFlags;
(function(ShapeFlags2) {
  ShapeFlags2[ShapeFlags2["ELEMENT"] = 1] = "ELEMENT";
  ShapeFlags2[ShapeFlags2["FUNCTIONAL_COMPONENT"] = 2] = "FUNCTIONAL_COMPONENT";
  ShapeFlags2[ShapeFlags2["STATEFUL_COMPONENT"] = 4] = "STATEFUL_COMPONENT";
  ShapeFlags2[ShapeFlags2["COMPONENT"] = 6] = "COMPONENT";
  ShapeFlags2[ShapeFlags2["TEXT_CHILDREN"] = 8] = "TEXT_CHILDREN";
  ShapeFlags2[ShapeFlags2["ARRAY_CHILDREN"] = 16] = "ARRAY_CHILDREN";
  ShapeFlags2[ShapeFlags2["SLOTS_CHILDREN"] = 32] = "SLOTS_CHILDREN";
  ShapeFlags2[ShapeFlags2["TELEPORT"] = 64] = "TELEPORT";
  ShapeFlags2[ShapeFlags2["SUSPENSE"] = 128] = "SUSPENSE";
  ShapeFlags2[ShapeFlags2["COMPONENT_SHOULD_KEEP_ALIVE"] = 256] = "COMPONENT_SHOULD_KEEP_ALIVE";
  ShapeFlags2[ShapeFlags2["COMPONENT_KEPT_ALIVE"] = 512] = "COMPONENT_KEPT_ALIVE";
})(ShapeFlags || (ShapeFlags = {}));
const getValueFromSlotsOrProps = (name, props, slots) => {
  if (slots == null ? void 0 : slots[name]) {
    return slots[name];
  }
  if (props == null ? void 0 : props[name]) {
    return () => props[name];
  }
  return void 0;
};
const isComponentInstance = (value) => {
  return (value == null ? void 0 : value.$) !== void 0;
};
const isElement = (vn) => {
  return Boolean(vn && vn.shapeFlag & 1);
};
const isComponent = (vn, type) => {
  return Boolean(vn && vn.shapeFlag & 6);
};
const isText = (vn, children) => {
  return Boolean(vn && vn.shapeFlag & 8);
};
const isNamedComponent = (child, name) => {
  return isComponent(child, child.type) && child.type.name === name;
};
const isTextChildren = (child, children) => {
  return Boolean(child && child.shapeFlag & 8);
};
const isArrayChildren = (vn, children) => {
  return Boolean(vn && vn.shapeFlag & 16);
};
const isSlotsChildren = (vn, children) => {
  return Boolean(vn && vn.shapeFlag & 32);
};
const getVNodeChildrenString = (vn) => {
  var _a, _b, _c;
  if (isText(vn, vn.children)) {
    return vn.children;
  }
  let text = "";
  if (isArrayChildren(vn, vn.children)) {
    for (const child of vn.children) {
      text += getVNodeChildrenString(child);
    }
  } else if (isSlotsChildren(vn, vn.children)) {
    const children = (_c = (_b = (_a = vn.children).default) == null ? void 0 : _b.call(_a)) != null ? _c : [];
    for (const child of children) {
      text += getVNodeChildrenString(child);
    }
  }
  return text;
};
const getChildrenFunc = (vn) => {
  if (isTextChildren(vn, vn.children) || isArrayChildren(vn, vn.children)) {
    return () => vn.children;
  }
  if (isSlotsChildren(vn, vn.children)) {
    return vn.children.default;
  }
  return void 0;
};
const getFirstComponent = (children) => {
  var _a, _b;
  if (!children) {
    return void 0;
  }
  for (const child of children) {
    if (isElement(child) || isComponent(child)) {
      return child;
    }
    if (isArrayChildren(child, child.children)) {
      const result = getFirstComponent(child.children);
      if (result)
        return result;
    } else if (isSlotsChildren(child, child.children)) {
      const children2 = (_b = (_a = child.children).default) == null ? void 0 : _b.call(_a);
      if (children2) {
        const result = getFirstComponent(children2);
        if (result)
          return result;
      }
    }
  }
  return void 0;
};
const getComponentNumber = (vNodes, componentName) => {
  let count = 0;
  for (const item of vNodes) {
    if (isComponent(item, item.type) && item.type.name === componentName) {
      count++;
    } else if (isArrayChildren(item, item.children)) {
      count += getComponentNumber(item.children, componentName);
    }
  }
  return count;
};
const isEmptyChildren = (children) => {
  if (!children) {
    return true;
  }
  for (const item of children) {
    if (item.children) {
      return false;
    }
  }
  return true;
};
const getChildrenComponents = (children, name, props, startIndex = 0) => {
  var _a, _b, _c;
  const result = [];
  for (const item of children) {
    if (isNamedComponent(item, name)) {
      if (props) {
        const index = startIndex + result.length;
        const extraProps = isFunction(props) ? props(item, index) : props;
        result.push(cloneVNode(item, extraProps, true));
      } else {
        result.push(item);
      }
    } else if (isArrayChildren(item, item.children)) {
      result.push(...getChildrenComponents(item.children, name, props, result.length));
    } else if (isSlotsChildren(item, item.children)) {
      const defaultChildren = (_c = (_b = (_a = item.children).default) == null ? void 0 : _b.call(_a)) != null ? _c : [];
      result.push(...getChildrenComponents(defaultChildren, name, props, result.length));
    }
  }
  return result;
};
const mergeFirstChild = (vns, extraProps) => {
  var _a, _b, _c;
  for (let i = 0; i < vns.length; i++) {
    const child = vns[i];
    if (isElement(child) || isComponent(child)) {
      const props = isFunction(extraProps) ? extraProps(child) : extraProps;
      vns[i] = cloneVNode(child, props, true);
      return true;
    }
    if (isArrayChildren(child, child.children)) {
      const result = mergeFirstChild(child.children, extraProps);
      if (result)
        return true;
    } else if (isSlotsChildren(child, child.children)) {
      const children = (_c = (_b = (_a = child.children).default) == null ? void 0 : _b.call(_a)) != null ? _c : [];
      const result = mergeFirstChild(children, extraProps);
      if (result) {
        vns[i] = createVNode(child, null, { default: () => children });
        return true;
      }
    }
  }
  return false;
};
const getBooleanProp = (value) => {
  return !!(value || isString(value));
};
const getAllElements = (vns) => {
  var _a, _b;
  const results = [];
  for (const vn of vns != null ? vns : []) {
    if (isElement(vn) || isComponent(vn) || isTextChildren(vn, vn.children)) {
      results.push(vn);
    } else if (isArrayChildren(vn, vn.children)) {
      results.push(...getAllElements(vn.children));
    } else if (isSlotsChildren(vn, vn.children)) {
      results.push(...getAllElements((_b = (_a = vn.children).default) == null ? void 0 : _b.call(_a)));
    }
  }
  return results;
};
function unFragment(nodeList) {
  function loop(nodes) {
    const unFragmentNodeList = [];
    nodes.forEach((node) => {
      var _a, _b;
      if (isVNode(node) && node.type === Fragment) {
        if (isSlotsChildren(node, node.children)) {
          unFragmentNodeList.push(...loop(((_b = (_a = node.children).default) == null ? void 0 : _b.call(_a)) || []));
        } else if (isArrayChildren(node, node.children)) {
          unFragmentNodeList.push(...loop(node.children));
        } else if (isString(node.children)) {
          unFragmentNodeList.push(node.children);
        }
      } else {
        unFragmentNodeList.push(node);
      }
    });
    return unFragmentNodeList;
  }
  return loop(nodeList);
}
const resolveProps = (vn) => {
  var _a, _b, _c, _d;
  const props = {};
  const options = (_b = (_a = vn.type) == null ? void 0 : _a.props) != null ? _b : {};
  for (const key of Object.keys((_c = vn.props) != null ? _c : {})) {
    const rawValue = (_d = vn.props) == null ? void 0 : _d[key];
    const camelKey = toCamelCase(key);
    let resolveValue = rawValue;
    if (rawValue === "" || rawValue === toKebabCase(camelKey)) {
      const type = isObject(options[camelKey]) ? options[camelKey].type : options[camelKey];
      if (type === Boolean) {
        resolveValue = true;
      }
    }
    props[camelKey] = resolveValue;
  }
  return props;
};
export { ShapeFlags, getAllElements, getBooleanProp, getChildrenComponents, getChildrenFunc, getComponentNumber, getFirstComponent, getVNodeChildrenString, getValueFromSlotsOrProps, isArrayChildren, isComponent, isComponentInstance, isElement, isEmptyChildren, isNamedComponent, isSlotsChildren, isText, isTextChildren, mergeFirstChild, resolveProps, unFragment };
